import { useEffect, useRef } from 'react';
import { atom, RecoilState, useSetRecoilState } from 'recoil';
import { usePrevious } from 'react-delta';
import * as K from 'common/constants';
import { SortOrder } from 'common/graphqlTypes';
import { recoilSelector } from 'common/util/recoil-selector';
import { EventCache, useEventCache } from 'hooks/useLRQ/useEventCache';
import { LRQRequest } from 'hooks/useLRQ/useLRQ';
import { settingsState } from 'components/common/Managers/SettingsManager.state';

/**
 * Atoms: Major Components Statuses
 */
export const sidebarStatus = atom<K.COMPONENT_STATUS>({
  key: K.RECOIL_ATOM_KEY.SIDEBAR_STATUS,
  default: K.COMPONENT_STATUS.IDLE,
});

export const logResultsTimelineStatus = atom<K.COMPONENT_STATUS>({
  key: K.RECOIL_ATOM_KEY.LOG_RESULTS_TIMELINE_STATUS,
  default: K.COMPONENT_STATUS.IDLE,
});

export const graphResultsStatus = atom<K.COMPONENT_STATUS>({
  key: K.RECOIL_ATOM_KEY.GRAPH_RESULTS_STATUS,
  default: K.COMPONENT_STATUS.IDLE,
});

export const logResultsStatus = atom<K.COMPONENT_STATUS>({
  key: K.RECOIL_ATOM_KEY.LOG_RESULTS_STATUS,
  default: K.COMPONENT_STATUS.IDLE,
});

/**
 * Selectors
 */
type SearchPageStatusReturnValue = {
  sidebar: K.COMPONENT_STATUS;
  timeline: K.COMPONENT_STATUS;
  logResults: K.COMPONENT_STATUS;
};

type SearchGraphPageStatusReturnValue = {
  sidebar: K.COMPONENT_STATUS;
  graphResults: K.COMPONENT_STATUS;
};

export const searchPageStatusSelector = recoilSelector<SearchPageStatusReturnValue>({
  key: K.RECOIL_SELECTOR_KEY.SEARCH_PAGE_STATUSES,
  get: ({ get }) => ({
    sidebar: get(sidebarStatus),
    timeline: get(logResultsTimelineStatus),
    logResults: get(logResultsStatus),
  }),
  set: ({ set }, newStatus) => {
    set(sidebarStatus, newStatus as unknown as K.COMPONENT_STATUS);
    set(logResultsTimelineStatus, newStatus as unknown as K.COMPONENT_STATUS);
    set(logResultsStatus, newStatus as unknown as K.COMPONENT_STATUS);
  },
});

export const searchGraphPageStatusSelector = recoilSelector<SearchGraphPageStatusReturnValue>({
  key: K.RECOIL_SELECTOR_KEY.SEARCH_GRAPH_PAGE_STATUSES,
  get: ({ get }) => ({
    sidebar: get(sidebarStatus),
    graphResults: get(graphResultsStatus),
  }),
  set: ({ set }, newStatus) => {
    set(sidebarStatus, newStatus as unknown as K.COMPONENT_STATUS);
    set(graphResultsStatus, newStatus as unknown as K.COMPONENT_STATUS);
  },
});

export const isSearchPageResultsPendingSelector = recoilSelector<boolean>({
  key: K.RECOIL_SELECTOR_KEY.SEARCH_PAGE_IS_PENDING,
  get: ({ get }) => {
    const majorSearchPageComponentStatuses = get(searchPageStatusSelector);
    const majorSearchGraphPageComponentStatuses = get(searchGraphPageStatusSelector);

    return Object
      .values({ ...majorSearchPageComponentStatuses, ...majorSearchGraphPageComponentStatuses })
      .includes(K.COMPONENT_STATUS.PENDING);
  },
});

/**
 * Sidebar States
 */

/**
 * Tracks the user action intent to display the facet details overlay. for the overlay to actually display,
 * {@see selectedFacetState} must also be truthy. Note that the state flow was preserved as-is when this
 * state was moved from local state to recoil.
 */
export const showFacetDetailsState = atom<boolean>({
  key: K.RECOIL_ATOM_KEY.SHOW_FACET_DETAILS,
  default: false,
});

/**
 * Tracks the currently selected facet. Its details are displayed in the facet details overlay.
 */
export const selectedFacetState = atom<string>({
  key: K.RECOIL_ATOM_KEY.SELECTED_FACET,
  default: '',
});

/**
 * Tracks whether the facet details overlay is displayed.
 */
export const isFacetDetailsVisibleState = recoilSelector<boolean>({
  key: K.RECOIL_SELECTOR_KEY.IS_FACET_DETAILS_VISIBLE,
  get: ({ get }) => {
    const showOverlay = get(showFacetDetailsState);
    const selectedFacet = get(selectedFacetState);
    return showOverlay && !!selectedFacet;
  },
});

/**
 * Tracks whether the sidebar expand/collapse toggle button is visible.
 */
export const isSidebarToggleVisibleState = recoilSelector<boolean>({
  key: K.RECOIL_SELECTOR_KEY.IS_SIDEBAR_TOGGLE_VISIBLE,
  get: ({ get }) => {
    // The sidebar toggle must always be visible when the sidebar is collapsed
    const showSidebar = get(settingsState)[K.SETTING.SHOW_SIDEBAR];
    if (!showSidebar) {
      return true;
    }
    // Otherwise, hide the sidebar toggle button when the facet details overlay is visible,
    // and show it when the facet details overlay is hidden.
    return !get(isFacetDetailsVisibleState);
  },
});

/**
 * Inspect Log Details States
 */

/**
 * Flag that indicates whether the inspect log details pane is currently pinned (true) or unpinned (false)
 */
export const pinLogDetailsState = atom<boolean>({
  key: K.RECOIL_ATOM_KEY.PIN_INSPECT_LOG_DETAILS,
  default: false,
});

// LRQ requests

export const anchorLogRequestsState = atom<LRQRequest[]>({
  key:     K.RECOIL_ATOM_KEY.ANCHOR_LOG_REQUESTS,
  default: K.EMPTY_ARRAY,
  dangerouslyAllowMutability: true,
});

export const initialLogRequestsState = atom<LRQRequest[]>({
  key:     K.RECOIL_ATOM_KEY.INITIAL_LOG_REQUESTS,
  default: K.EMPTY_ARRAY,
  dangerouslyAllowMutability: true,
});

export const paginatedLogRequestsState = atom<LRQRequest[]>({
  key:     K.RECOIL_ATOM_KEY.PAGINATED_LOG_REQUESTS,
  default: K.EMPTY_ARRAY,
  dangerouslyAllowMutability: true,
});

export const plotDataRequestsState = atom<LRQRequest[]>({
  key:     K.RECOIL_ATOM_KEY.PLOT_DATA_REQUESTS,
  default: K.EMPTY_ARRAY,
  dangerouslyAllowMutability: true,
});

export const facetValuesRequestsState = atom<LRQRequest[]>({
  key:     K.RECOIL_ATOM_KEY.FACET_VALUES_REQUESTS,
  default: K.EMPTY_ARRAY,
  dangerouslyAllowMutability: true,
});

export const powerQueryRequestsState = atom<LRQRequest[]>({
  key:     K.RECOIL_ATOM_KEY.POWER_QUERY_REQUESTS,
  default: K.EMPTY_ARRAY,
  dangerouslyAllowMutability: true,
});

/**
 * Log Results Scrolling States
 */

/**
 * This state tracks whether the scroll state is ready for threshold calculations in the onItemsRendered callback
 */
export const isScrollReadyState = atom<boolean>({
  key: K.RECOIL_ATOM_KEY.LOG_RESULTS_SCROLL_READY,
  default: false,
});

/**
 * Tracks the sort order for events. Events can either be sorted with the most recent events at the bottom
 *   (like a terminal), or with the most recent events at the top (like an email client).
 */
export const eventSortOrderState = recoilSelector<SortOrder>({
  key: K.RECOIL_SELECTOR_KEY.EVENT_SORT_ORDER,
  get: ({ get }) => get(settingsState)[K.SETTING.SORT_ORDER],
});

/**
 * Tracks whether the user has scrolled to the top or bottom of the result set.
 *  This state is tied to physical scroll direction, not time.
 *  This is extracted into its own atom state to optimize for perf. (1 update on top/bottom VS multiple on each scroll event)
 */
export type LogResultsScrollExtremes = {
  isAtTheTop: boolean;
  isAtTheBottom: boolean;
};

export const logResultsScrollExtremes = atom<LogResultsScrollExtremes>({
  key: K.RECOIL_ATOM_KEY.LOG_RESULTS_SCROLL_EXTREMES,
  default: {
    isAtTheTop: false,
    isAtTheBottom: false,
  },
});

/**
 * Tracks whether the user has scrolled to the chronological start of the result set.
 *  This state is tied to time, not physical scroll direction.
 */
export const isAtStartTimeState = recoilSelector<boolean>({
  key: K.RECOIL_SELECTOR_KEY.IS_AT_START_TIME,
  get: ({ get }) => {
    const sortOrder = get(eventSortOrderState);
    const scrollExtremes = get(logResultsScrollExtremes);
    return (sortOrder === SortOrder.MOST_RECENT_AT_BOTTOM && scrollExtremes.isAtTheTop)
      || (sortOrder === SortOrder.MOST_RECENT_AT_TOP && scrollExtremes.isAtTheBottom);
  },
});

/**
 * Tracks whether the user has scrolled to the chronological end of the result set.
 *  This state is tied to time, not physical scroll direction.
 */
export const isAtEndTimeState = recoilSelector<boolean>({
  key: K.RECOIL_SELECTOR_KEY.IS_AT_END_TIME,
  get: ({ get }) => {
    const sortOrder = get(eventSortOrderState);
    const scrollExtremes = get(logResultsScrollExtremes);
    return (sortOrder === SortOrder.MOST_RECENT_AT_BOTTOM && scrollExtremes.isAtTheBottom)
      || (sortOrder === SortOrder.MOST_RECENT_AT_TOP && scrollExtremes.isAtTheTop);
  },
});

/**
 * Hooks
 */
export function useProgressiveStateManagement(
  eventCache: EventCache,
  eventCacheIds: string[],
  componentStatusAtom: RecoilState<K.COMPONENT_STATUS>,
  returnPreviousCache: boolean,
): EventCache {
  const setComponentStatus = useSetRecoilState(componentStatusAtom);
  const prevEventCacheIds = usePrevious(eventCacheIds);
  const prevEventCache = useEventCache(prevEventCacheIds);
  const prevEventCacheStableRef = useRef<EventCache>();

  // Handle component status
  useEffect(() => {
    if (returnPreviousCache) {
      setComponentStatus(K.COMPONENT_STATUS.PENDING);
    } else {
      setComponentStatus(K.COMPONENT_STATUS.RESOLVED);
    }
  }, [returnPreviousCache, setComponentStatus]);

  // Save previous snapshot of the cache if cache changes (e.g. when user issues a new request)
  if (prevEventCacheIds !== undefined && (JSON.stringify(prevEventCacheIds) !== JSON.stringify(eventCacheIds))) {
    prevEventCacheStableRef.current = prevEventCache;
  }

  // Return previous version of the cache on demand
  // common case: set `returnPreviousCache` to true when new eventCache is pending(loading)
  if (returnPreviousCache && prevEventCacheStableRef.current !== undefined) {
    eventCache = prevEventCacheStableRef.current;
  }

  return eventCache;
}
